import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { X, Bell, CreditCard, Wallet, ListChecks, RefreshCw } from "lucide-react";
import { useUiStore } from "@/stores/ui.store";
import { cn } from "@/lib/cn";
import { Button } from "@/ui";
import type { NotificationType, NotifSeverity } from "@/types";
import { useNotifications, useMarkNotificationRead, useMarkAllNotificationsRead } from "./notifications.hooks";
import { notificationTarget } from "./notification-link.logic";

const ICONS: Record<NotificationType, typeof Bell> = {
  PAYMENT_PENDING: CreditCard,
  WALLET_LOW: Wallet,
  TASK_OVERDUE: ListChecks,
  SUBSCRIPTION_RENEWAL: RefreshCw,
  GENERIC: Bell,
};

const SEV_COLOR: Record<NotifSeverity, string> = {
  INFO: "text-info bg-info-dim",
  ALERT: "text-warning bg-warning-dim",
  CRITICAL: "text-danger bg-danger-dim",
};

type Filter = "all" | "ALERT" | "CRITICAL";

export function NotificationsDrawer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const open = useUiStore((s) => s.notificationsOpen);
  const setOpen = useUiStore((s) => s.setNotificationsOpen);
  const { notifications: items, isLoading } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();
  const [filter, setFilter] = useState<Filter>("all");

  const visible = useMemo(
    () => (filter === "all" ? items : items.filter((n) => n.severity === filter)),
    [items, filter],
  );
  const unread = items.filter((n) => !n.read).length;

  if (!open) return null;

  const markAllRead = () => markAll.mutate();
  // Toute notification qui référence une entité navigue vers elle (ou vers sa
  // liste). Seul "Task" le faisait ; les 8 autres types émis par le backend
  // étaient des clics morts.
  const onItemClick = (
    id: string,
    read: boolean,
    entityType?: string,
    entityId?: string,
    type?: string,
  ) => {
    if (!read) markRead.mutate(id);
    const target = notificationTarget(entityType, entityId, type);
    if (target) {
      setOpen(false);
      navigate(target);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-[990] bg-black/55 animate-fadeIn" onClick={() => setOpen(false)} />
      <aside className="fixed end-0 top-0 z-[991] h-[var(--app-h)] w-full md:w-[380px] bg-card border-s border-border2 shadow-floating flex flex-col animate-slideIn">
        <div className="flex items-center justify-between px-5 h-[var(--topbar-h)] border-b border-border">
          <div className="flex items-center gap-2 font-display font-bold text-text">
            <Bell className="h-4 w-4 text-warning" />
            {t("notifications.title")} {unread > 0 && <span className="text-[11px] text-text3">({unread})</span>}
          </div>
          <button
            onClick={() => setOpen(false)}
            className="flex items-center justify-center h-9 w-9 rounded-sm text-text2 hover:bg-bg4 hover:text-text"
            aria-label="Fermer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex items-center gap-2 px-5 py-3 border-b border-border">
          {(["all", "ALERT", "CRITICAL"] as Filter[]).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={cn(
                "h-7 px-3 rounded-pill text-[11px] font-semibold border transition-all",
                filter === f
                  ? "bg-primary text-white border-primary"
                  : "bg-bg3 text-text2 border-border hover:bg-bg4 hover:text-text",
              )}
            >
              {f === "all" ? t("notifications.all") : f === "ALERT" ? t("notifications.alert") : t("notifications.critical")}
            </button>
          ))}
          <Button variant="ghost" size="xs" className="ml-auto" onClick={markAllRead}>
            {t("notifications.markAll")}
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto scroll-thin px-3 py-2">
          {isLoading ? (
            <div className="text-center text-xs text-text3 py-12">{t("notifications.loading")}</div>
          ) : visible.length === 0 ? (
            <div className="text-center text-xs text-text3 py-12">{t("notifications.empty")}</div>
          ) : (
            visible.map((n) => {
              const Icon = ICONS[n.type];
              return (
                <div
                  key={n.id}
                  onClick={() => onItemClick(n.id, n.read, n.entityType, n.entityId, n.type)}
                  className={cn(
                    "flex items-start gap-3 px-3 py-3 rounded-sm transition-colors hover:bg-bg3/70",
                    (!n.read || notificationTarget(n.entityType, n.entityId, n.type)) && "cursor-pointer",
                    !n.read && "bg-white/[0.02]",
                  )}
                >
                  <div className={cn("flex items-center justify-center h-7 w-7 rounded-lg shrink-0", SEV_COLOR[n.severity])}>
                    <Icon className="h-3.5 w-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-semibold text-text">{n.title}</div>
                    {n.body && <div className="text-[11px] text-text2 mt-0.5 truncate">{n.body}</div>}
                    <div className="text-[10px] text-text3 mt-0.5">{n.at}</div>
                  </div>
                  {!n.read && <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />}
                </div>
              );
            })
          )}
        </div>
      </aside>
    </>
  );
}
